import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Descriptions, Tag, message, Card, Spin, Space,
  Modal, Form, Input, Select, Switch, Avatar,
} from 'antd';
import { ArrowLeftOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { usersApi } from '../services/api-services';

const { TextArea } = Input;

const roleMap: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  app_admin: '应用管理员',
  user: '普通用户',
};

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const users: any = await usersApi.list();
      const found = users.find((u: any) => u.id === Number(id));
      setUser(found || null);
    } catch {
      message.error('获取用户信息失败');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  const openEditModal = () => {
    editForm.setFieldsValue({
      name: user.name,
      role: user.role,
      avatar: user.avatar || '',
      title: user.title || '',
      bio: user.bio || '',
      feishu_id: user.feishu_id || '',
      expertise_tags: user.expertise_tags || [],
      is_expert: user.is_expert || false,
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async (values: any) => {
    try {
      await usersApi.update(Number(id), values);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '更新失败');
    }
  };

  if (loading && !user) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到用户</p>
          <Button type="primary" onClick={() => navigate('/user-management')}>返回列表</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/user-management')} />
          <Avatar
            size={64}
            src={user.avatar}
            icon={!user.avatar && <UserOutlined />}
            style={{ backgroundColor: 'var(--apple-blue)', flexShrink: 0 }}
          >
            {user.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <h1 className="apple-page-title" style={{ marginBottom: 4 }}>{user.name}</h1>
            <Tag color="blue">{roleMap[user.role] || user.role}</Tag>
            {user.is_expert && <Tag color="green">专家</Tag>}
          </div>
        </Space>
        <Button icon={<EditOutlined />} onClick={openEditModal}>编辑</Button>
      </div>

      <Card>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="用户名">{user.username}</Descriptions.Item>
          <Descriptions.Item label="姓名">{user.name}</Descriptions.Item>
          <Descriptions.Item label="角色">
            <Tag color="blue">{roleMap[user.role] || user.role}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="头像">
            {user.avatar ? <img src={user.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} /> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="职称 / 头衔">{user.title || '-'}</Descriptions.Item>
          <Descriptions.Item label="简介">{user.bio || '-'}</Descriptions.Item>
          <Descriptions.Item label="飞书 ID">{user.feishu_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="专家身份">
            {user.is_expert ? <Tag color="green">是</Tag> : '否'}
          </Descriptions.Item>
          <Descriptions.Item label="专业领域">
            {user.expertise_tags && user.expertise_tags.length > 0
              ? user.expertise_tags.map((tag: string) => <Tag key={tag} color="blue" style={{ margin: '2px 4px 2px 0' }}>{tag}</Tag>)
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {user.created_at ? new Date(user.created_at).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        className="apple-modal"
        title="编辑用户"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        width={640}
        destroyOnHidden
      >
        <Form className="apple-form" form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select options={[
              { label: '超级管理员', value: 'super_admin' },
              { label: '管理员', value: 'admin' },
              { label: '应用管理员', value: 'app_admin' },
              { label: '普通用户', value: 'user' },
            ]} />
          </Form.Item>
          <Form.Item name="avatar" label="头像 URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="title" label="职称 / 头衔">
            <Input placeholder="如：首席架构师、教授" />
          </Form.Item>
          <Form.Item name="bio" label="简介">
            <TextArea rows={3} placeholder="用户简介" />
          </Form.Item>
          <Form.Item name="feishu_id" label="飞书 ID">
            <Input placeholder="飞书用户 ID" />
          </Form.Item>
          <Form.Item name="is_expert" label="标记为专家" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="expertise_tags" label="专业领域">
            <Select mode="tags" placeholder="输入后回车添加标签" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDetail;
